"use client";

import * as React from "react";
import { Ship, Settings, Package, Globe } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { useEffect, useRef, useState } from "react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const capabilities = [{
  title: "Maritime Brokerage & Procurement Coordination",
  description: "Coordinating procurement of spare parts and technical supplies through a trusted supplier network for commercial vessels.",
  icon: Ship
}, {
  title: "Technical Service Coordination",
  description: "Facilitating technical service coordination, including spare parts, equipment, and maintenance, with reliable maritime partners.",
  icon: Settings
}, {
  title: "Port Logistics Coordination",
  description: "Managing port logistics coordination to streamline turnaround times and optimize vessel operations.",
  icon: Package
}, {
  title: "Global Maritime Partner Network",
  description: "Connecting vessels with trusted maritime suppliers and service providers worldwide through our global network.",
  icon: Globe
}];
export default function CoreMaritimeCapabilities() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="core-maritime-capabilities" className="w-full py-24 bg-background text-foreground">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 text-center md:text-left">
          <h2 className="text-3xl md:text-4xl font-serif font-bold tracking-tight mb-4">
            Core Maritime Capabilities
          </h2>
          <div className="h-1 w-20 bg-ring mx-auto md:mx-0"></div>
        </div>

        <div ref={ref} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {capabilities.map((capability, i) => {
          const Icon = capability.icon;
          return <Card key={i} data-index={i} className="group bg-transparent border-border rounded-none hover:bg-primary/5 transition-colors duration-500 flex flex-col h-full">
                <CardHeader className="pb-4">
                  <div className="mb-6">
                    <Icon className="w-10 h-10 text-muted-foreground group-hover:text-ring transition-colors duration-500" strokeWidth={1.5} />
                  </div>
                  <CardTitle className="font-serif text-[18px] leading-tight">
                    {capability.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 mt-auto">
                  <CardDescription className="font-sans text-[14px] leading-relaxed !text-left !text-[hsl(0,0%,0%)]">
                    {capability.description}
                  </CardDescription>
                </CardContent>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}